import store from 'redux/store';
import { deleteEvent, raiseFileEvent, raiseProcessEvent } from 'redux/slices/eventsSlice';
import { isFileEventType } from 'utils/eventTypes';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditEvent } from './edit/showEditEvent';

type EventEntity = Extract<ActionEntity, { type: 'event' }>;

const isAllEvent = (entities: readonly ActionEntity[]): entities is readonly EventEntity[] => entities.every((entity) => entity.type === 'event');

/**
 * Action definitions for events. `createEvent` / `addToTransportEvent` are
 * scoped; `edit` / `raiseEvent` / `showInstances` / `delete` are contextual and
 * single. `raiseEvent` is hidden unless the one event is a file or process
 * event (dispatching the matching raise thunk); `showInstances` navigates to the
 * file- or process-instances route by type.
 */
export const eventActionDefinitions: ActionDefinitions = [
  {
    id: 'createEvent',
    scope: 'type',
    label: 'New Event',
    resolve: (ctx) => () => void showEditEvent({ mode: 'create', createIn: ctx.createIn }).then(ctx.refresh),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllEvent(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditEvent({ mode: 'edit', id: only.id }).then(ctx.refresh);
    },
  },
  {
    id: 'raiseEvent',
    label: 'Raise Event',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllEvent(entities)) return 'hidden';
      if (entities.length !== 1) return 'hidden';
      const [only] = entities;
      if (isFileEventType(only.eventType)) {
        return () => void store.dispatch(raiseFileEvent({ event_id: only.id })).then(() => refresh());
      }
      if (only.eventType === 'processEvent') {
        return () => void store.dispatch(raiseProcessEvent({ event_id: only.id })).then(() => refresh());
      }
      return 'hidden';
    },
  },
  {
    id: 'showInstances',
    label: 'Show instances',
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllEvent(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () => {
        const zone = encodeURIComponent(only.zoneName || 'unknown');
        const eventName = encodeURIComponent(only.name || 'unknown');
        if (isFileEventType(only.eventType)) navigate(`/automation/file_event_instances/${zone}/${eventName}`);
        else if (only.eventType === 'processEvent') navigate(`/automation/event_instances/${zone}/${eventName}`);
      };
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllEvent(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      const single = entities.length === 1 ? entities[0] : null;
      return deleteExecutor(entities, (entity) => store.dispatch(deleteEvent({ zone_name: entity.zoneName, name: entity.name })), {
        refresh,
        confirmTitle: 'Delete event',
        confirmBody: single ? `Delete event "${single.name}" in zone "${single.zoneName}"?` : `Delete ${entities.length} events?`,
      });
    },
  },
  {
    id: 'addToTransportEvent',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllEvent(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'event',
        entities.map((entity) => entity.id),
        { objectLabel: single ? `${single.zoneName || '-'} | ${single.name}` : `${entities.length} events` }
      );
    },
  },
];
